import * as React from 'react';
import { uuid } from 'uuidv4';
import { Button } from '../../../../components/button/Button';
import { PopoverButton } from '../../../../components/button/PopoverButton';
import ResultDialog from '../../../../components/dialog-types/ResultDialog';
import { ToggleSwitch } from '../../../../components/form-elements/ToggleSwitch';
import { StickyActionBar } from '../../../../components/sticky-action-bar/StickyActionBar';
import { AppUtils } from '../../../../utils/app-utils';
import { OrderUtils } from '../../../../utils/order-utils';
import { OrderGroupRunningTotal } from '../../../../transportation/models/planning/order-group-running-total';
import { OrderSummary } from '../../../../transportation/models/shared/order-summary';
import { OrderGroupSelectable } from './OrderGroupSelectable';

interface IProps {
    orders: OrderSummary[];
    showAllByDefault: boolean;
}

interface IGroupOption {
    id: number;
    display: string;
}

let IGNORE_UNMOUNT_SELECTABLE = false;

export const DeliveryGroupControl = (props: IProps) => {

    const [orderList, setOrderList] = React.useState<OrderSummary[]>([]);
    const [currentGroupId, setCurrentGroupId] = React.useState(0);
    const [orderGroups, setOrderGroups] = React.useState({} as any);
    const [hasSelected, setHasSelected] = React.useState(false);
    const [groupList, setGroupList] = React.useState([] as IGroupOption[]);
    const [groupTotals, setGroupTotals] = React.useState([] as OrderGroupRunningTotal[]);
    const [runningTotals, setRunningTotals] = React.useState(new OrderGroupRunningTotal());
    const [showAllColumns, setShowAllColumns] = React.useState(false);
    
    const toggleExtraData = () => {
        setShowAllColumns(!showAllColumns);
    };

    /**
     * Make a copy of the order list in our control
     * for playing with
     */
    React.useEffect(() => {
        if (props.orders && props.orders.length > 0) {

            const max = props.orders.reduce((accum: number, itm: OrderSummary) => {
                accum = itm.groupId > accum ? itm.groupId : accum;
                return accum;
            }, 0);

            updateGroupingInfo(props.orders, max);
        }
    }, [props.orders]);

    /**
     * Update the column layout baesd on the checkbox
     */
    React.useEffect(() => {
        setShowAllColumns(props.showAllByDefault);
    }, [props.showAllByDefault]);

    /**
     * Watch the order list and update the state
     * for whether there are any orders selected
     */
    React.useEffect(() => {
        if (orderList) {
            const anySelected = orderList.some((itm: OrderSummary) => {
                return itm.isChecked;
            });

            setHasSelected(anySelected);
        }
    }, [orderList]);

    /**
     * Sort function for sorting the order list by group ID
     * in ascending order
     * @param a The first order to compare
     * @param b The second order to compare
     */
    const groupCompare = (a: OrderSummary, b: OrderSummary) => {
        if (a.groupId < b.groupId) {
            return -1;
        }
        if (a.groupId > b.groupId) {
            return 1;
        }
        return 0;
    };

    /**
     * Take the selected orders and make a new group for 
     * them.
     */
    const makeGroup = () => {

        IGNORE_UNMOUNT_SELECTABLE = true;

        const tmpOrders = [...orderList];
        const nextGroupId = currentGroupId + 1;

        // Update the group ID for everything currently selected
        tmpOrders.forEach((itm: OrderSummary) => {
            if (itm.isChecked) {
                itm.groupId = nextGroupId;
            }
            itm.isChecked = false;
        });

        updateGroupingInfo(tmpOrders, nextGroupId);
        setRunningTotals(new OrderGroupRunningTotal());
    };

    /**
     * Take the selected orders and make a new group for 
     * them.
     */
    const moveOrders = (groupId: number) => {

        IGNORE_UNMOUNT_SELECTABLE = true;

        let tmpOrders = [...orderList];

        tmpOrders = tmpOrders.sort(OrderUtils.PlanningGroupCompare);

        // Update the group ID for everything currently selected
        tmpOrders.forEach((itm: OrderSummary) => {
            if (itm.isChecked) {
                itm.groupId = groupId;
            }
            itm.isChecked = false;
        });

        let oldGrpId = 0;
        let newGrpId = 0;
        const sortedAry = tmpOrders.sort(groupCompare);
        const minValue = sortedAry[0].groupId;

        sortedAry.forEach((itm: OrderSummary) => {
            if (oldGrpId === 0 && itm.groupId === minValue) {
                itm.groupId = newGrpId;
            }
            else if (oldGrpId === itm.groupId) {
                itm.groupId = newGrpId;
            }
            else {
                oldGrpId = itm.groupId;
                newGrpId = newGrpId + 1;
                itm.groupId = newGrpId;
            }

            itm.isChecked = false;
        });

        updateGroupingInfo(sortedAry, newGrpId);
        setRunningTotals(new OrderGroupRunningTotal());
    };

    /**
     * Prompt the user to confirm they want to add all the 
     * currently selected orders to the provided group
     * @param groupId The ID of the group to add the selected orders to
     */
    const showAddToGroupDialog = async (groupId?: number) => {

        if (groupId || groupId === 0) {
            const result = await ResultDialog.show({
                title: 'Add to Group?',
                message: 'Are you sure you want to add these orders to this group?'
            });

            if (result) {
                moveOrders(groupId);
            }
        }
    };

    /**
     * Re-build the list of group options up to the index 
     * that is passed in
     * @param maxId The highest group ID we currently have
     */
    const updateGroupList = (maxId: number) => {
        const groupOpts = [];

        for (let i = 0; i <= maxId; i++) {
            groupOpts.push({
                id: i,
                display: `Add to Group #${i + 1}`
            });
        }

        setCurrentGroupId(maxId);
        setGroupList(groupOpts);
    };

    /**
     * Update our order list with the selected orders
     * when the user is done clicking/dragging
     * @param evt The list of selectable items that are picked
     */
    const onDragComplete = (evt: any) => {

        // The select library we use calls dragComplete
        // whenever a component is unmounted.  However,
        // that causes issues.  Hacky, but it's what's 
        // required without changing implemenation.
        if (IGNORE_UNMOUNT_SELECTABLE) {
            return;
        }

        const o = [...orderList];

        const tempGroups : any = {};

        for (let i = 0; i < o.length; i++) {

            let foundEvtMatch = false;
            const orderItem = o[i];

            for (let j = 0; j < evt.length; j++) {
                const evtItem = evt[j];
                if (evtItem.props.orderItem.id === orderItem.id) {
                    foundEvtMatch = true;
                    break;
                }
            }

            o[i].isChecked = (foundEvtMatch) ? true : false;

            const adjustedGroupId = orderItem.groupId + 1;
            if (!tempGroups.hasOwnProperty(adjustedGroupId)) {
                tempGroups[adjustedGroupId] = [];
            }

            if (!foundEvtMatch) {
                tempGroups[adjustedGroupId].push(orderItem);
            }
        }

        // Recalcualte all the order groupings based on selecteds
        _buildTemporaryGroupTotals(tempGroups);

        // Update the running totals
        const selecteds = o.filter((itm: OrderSummary) => {
            return itm.isChecked;
        });

        const rollup = getGroupTotals(selecteds);
        setRunningTotals(rollup);

        // Update the order list
        setOrderList(o);
    };

    /**
     * Cleanup activity to refresh the group state
     * @param tmpOrders List of orders to group
     * @param nextGroupId The next group ID to be used
     */
    const updateGroupingInfo = (tmpOrders: OrderSummary[], nextGroupId: number) => {

        setOrderList(tmpOrders);
        updateGroupList(nextGroupId);

        const grps = AppUtils.groupBy(tmpOrders, (i: OrderSummary) => i.groupId);
        const grpTotalList : OrderGroupRunningTotal[] = [];

        Object.keys(grps).forEach((propName: string) => {
            const rollup = getGroupTotals(grps[propName], (Number.parseInt(propName, 10) + 1));
            grps[propName].rollup = rollup;
            grps[propName].uid = uuid();
            grpTotalList.push(rollup);
        });

        setOrderGroups(grps);
        setGroupTotals(grpTotalList);

        setTimeout(() => {
            IGNORE_UNMOUNT_SELECTABLE = false;
        }, 200);

    };

    /**
     * Rollup and return a list of metrics based on the
     * provided order summary list
     * @param orders A list of orders to roll up
     */
    const getGroupTotals = (orders: OrderSummary[], groupId? : number) : OrderGroupRunningTotal => {

        let unitCount = 0;
        let weightCount = 0;
        let singleCount = 0;
        let feetCount = 0;
        let sectionCount = 0;
        let trackCount = 0;
        let hardwareCount = 0;

        orders.forEach((itm: OrderSummary) => {
            unitCount += itm.units;
            weightCount += itm.weight;
            singleCount += itm.singles;
            feetCount += itm.feet;
            sectionCount += itm.sections;
            trackCount += itm.track;
            hardwareCount += itm.hardware;
        });

        return {
            groupId: (groupId !== undefined) ? groupId : -1,
            units: unitCount,
            weight: weightCount,
            singles: singleCount,
            feet: feetCount,
            sections: sectionCount,
            track: trackCount,
            hardware: hardwareCount
        };
    };

    /**
     * Builds the temporary list of running totals for all groups
     * factoring out the selected orders
     * @param tempGroups A loose object of order summaries grouped by groupId
     */
    const _buildTemporaryGroupTotals = (tempGroups : any) => {
        const newTempRollUps : OrderGroupRunningTotal[] = [];
        Object.keys(tempGroups).forEach((itm : string) => {
            const groupId = Number.parseInt(itm, 10);
            const newTotals = getGroupTotals(tempGroups[itm], groupId);
            newTempRollUps.push(newTotals);
        });

        setGroupTotals(newTempRollUps);
    };

    return <React.Fragment>

        <div className="row">
            <div className="col-md-12">
                <ToggleSwitch
                    id="delivery-group-check"
                    checked={showAllColumns}
                    onClick={toggleExtraData}
                    label="Show All Columns"
                />
            </div>
        </div>

        <OrderGroupSelectable
            orderGroups={orderGroups}
            showExtraData={showAllColumns}
            onDragComplete={onDragComplete}
        />

        <StickyActionBar classes="order-group-total" showBar={true}>

            <div className="group-roll-ups">
                <table className="table striped">
                    <thead>
                        <tr>
                            <th></th>
                            <th>Units</th>
                            <th>Weight</th>
                            <th>Singles</th>
                            <th>Feet</th>
                            {
                                showAllColumns
                                    ? <React.Fragment>
                                        <th>Sections</th>
                                        <th>Track</th>
                                        <th>Hardware</th>
                                    </React.Fragment>
                                    : null
                            }
                        </tr>
                    </thead>
                    <tbody>                
                        {
                            groupTotals && groupTotals.length > 0
                                ? groupTotals.map((item : OrderGroupRunningTotal) => 
                                    <tr key={`${item.groupId}-table-row`}>
                                        <td>{ `Group #${ item.groupId }` } </td>
                                        <td>{item.units}</td>
                                        <td>{item.weight}</td>
                                        <td>{item.singles}</td>
                                        <td>{item.feet}</td>
                                        {
                                            showAllColumns
                                                ? <React.Fragment>         
                                                    <td>{item.sections}</td>
                                                    <td>{item.track}</td>
                                                    <td>{item.hardware}</td>
                                                </React.Fragment>
                                                : null
                                        }
                                    </tr>
                                )
                                : null
                        }

                        <tr key="selected-table-row">
                            <td>Selected</td>
                            <td>{runningTotals.units}</td>
                            <td>{runningTotals.weight}</td>
                            <td>{runningTotals.singles}</td>
                            <td>{runningTotals.feet}</td>
                            {
                                showAllColumns
                                    ? <React.Fragment>         
                                        <td>{runningTotals.sections}</td>
                                        <td>{runningTotals.track}</td>
                                        <td>{runningTotals.hardware}</td>
                                    </React.Fragment>
                                    : null
                            }
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="group-total-btns">
                <Button
                    testID="delivery-group-new-group"
                    disable={ !hasSelected }
                    text="New Group"
                    icon="la-magic"
                    onBtnClick={ makeGroup } />

                <PopoverButton
                    label="Move to Group"
                    testID="delivery-group-make-group"
                    isDisabled={ !(groupList.length > 1 && hasSelected) }
                    onItemSelected={showAddToGroupDialog}
                    listItems={groupList}
                />

            </div>

        </StickyActionBar>

    </React.Fragment>;
};
